import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BlogContext } from '../BlogContext';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';

const AdminMainOverview = () => {
  const { posts, loading } = useContext(BlogContext);
  const [stats, setStats] = useState({
    totalPosts: 0,
    totalViews: 0,
    totalContacts: 0,
    pendingTodos: 0
  });

  // Fetch data for overview stats
  useEffect(() => {
    const fetchOverviewData = async () => {
      try {
        // Fetch contacts
        const contactsSnapshot = await getDocs(collection(db, 'contacts'));
        const contactsList = contactsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Fetch todos
        const todosSnapshot = await getDocs(collection(db, 'todos'));
        const todosList = todosSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Calculate stats
        const totalViews = posts.reduce((sum, post) => sum + (Number(post.views) || 0), 0);
        const unreadContacts = contactsList.filter(contact =>
          contact.status !== 'spam' && contact.status !== 'responded'
        );
        const pendingTodos = todosList.filter(todo => !todo.completed).length;

        setStats({
          totalPosts: posts.length,
          totalViews,
          totalContacts: unreadContacts.length,
          pendingTodos
        });
      } catch (error) {
        console.error('Error fetching overview data:', error);
      }
    };

    if (!loading) {
      fetchOverviewData();
    }
  }, [posts, loading]);

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="text-center">
        <h1 className="text-4xl font-bold mb-2">Dashboard Overview</h1>
        <p className="text-gray-400">Manage your portfolio from one central location</p>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Blog Stats */}
        <Link to="/admin/blog-dashboard" className="block">
          <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6 hover:bg-[#2a2a2a] transition-colors">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-600 rounded-lg">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
                  <path fillRule="evenodd" d="M4 5a2 2 0 012-2v1a1 1 0 002 0V3a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
                </svg>
              </div>
              <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"/>
              </svg>
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-bold">Blog Dashboard</h3>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Posts:</span>
                <span className="text-blue-400">{stats.totalPosts}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Views:</span>
                <span className="text-green-400">{stats.totalViews}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Messages:</span>
                <span className="text-purple-400">{stats.totalContacts}</span>
              </div>
            </div>
          </div>
        </Link>

        {/* To-Do Stats */}
        <Link to="/admin/todo-dashboard" className="block">
          <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6 hover:bg-[#2a2a2a] transition-colors">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-orange-600 rounded-lg">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 8a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2z" clipRule="evenodd"/>
                </svg>
              </div>
              <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"/>
              </svg>
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-bold">To-Do List</h3>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Pending:</span>
                <span className="text-orange-400">{stats.pendingTodos}</span>
              </div>
              <p className="text-sm text-gray-400">Manage your tasks</p>
            </div>
          </div>
        </Link>

        {/* School Placeholder */}
        <Link to="/admin/school-dashboard" className="block">
          <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6 hover:bg-[#2a2a2a] transition-colors">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-600 rounded-lg">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 10-.788-1.84L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.84l-7-3z"/>
                  <path d="M3.31 9.397L5.073 10.13a1 1 0 00.787 0l1.94-.831-2.94-1.257a.999.999 0 01-.356-.257l-2.44-1.063a1 1 0 00-.788 1.84z"/>
                  <path fillRule="evenodd" d="M14.438 10.688L12.5 11.519l-2.94-1.257a.999.999 0 01-.356-.257l-2.44-1.063a1 1 0 00-.788 1.84L7.667 12.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.84l-1.262-.569a.999.999 0 01-.356-.257z" clipRule="evenodd"/>
                </svg>
              </div>
              <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"/>
              </svg>
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-bold">School Dashboard</h3>
              <p className="text-sm text-gray-400">Coming Soon</p>
              <p className="text-xs text-gray-500">Academic management tools</p>
            </div>
          </div>
        </Link>

        {/* Quick Actions Card */}
        <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
          <div className="p-3 bg-purple-600 rounded-lg w-fit mb-4">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H9a1 1 0 010 2H7.771l.062-.062L8.157 12zM11 12a1 1 0 01.707 1.707l.353.353.062.062H11a1 1 0 010-2z" clipRule="evenodd"/>
            </svg>
          </div>
          <h3 className="text-lg font-bold mb-2">Quick Actions</h3>
          <div className="space-y-2">
            <Link
              to="/admin/blog"
              className="block text-sm text-blue-400 hover:text-blue-300"
            >
              Create New Post →
            </Link>
            <Link
              to="/admin/contacts"
              className="block text-sm text-purple-400 hover:text-purple-300"
            >
              View Messages →
            </Link>
          </div>
        </div>
      </div>

      {/* Recent Activity Summary */}
      <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
        <h2 className="text-xl font-bold mb-4">Recent Activity</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-400">{stats.totalPosts}</div>
            <div className="text-sm text-gray-400">Blog Posts</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-400">{stats.totalViews}</div>
            <div className="text-sm text-gray-400">Total Views</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-400">{stats.totalContacts}</div>
            <div className="text-sm text-gray-400">Unread Messages</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminMainOverview;
